import React from 'react';
import {useThemeContext, type ThemeColors} from '@packages/utilities/styled';
import kebabCase from 'lodash/kebabCase';

const canvasBackgroundKey = 'uiCanvasBackground' satisfies keyof ThemeColors;
const UI_CANVAS_BACKGROUND_CSS_VAR = `--colors-${kebabCase(canvasBackgroundKey)}`;

/**
 * Static fallback in the event that the computed CSS variable value cannot be read from the parent document.
 * Matches the `darkDefaultTheme` value for `uiCanvasBackground` (d2.gray[800] in the palette).
 */
const CANVAS_BACKGROUND_FALLBACK = '#404040';

/**
 * Reads the resolved value of the `uiCanvasBackground` Spring color token
 * from the parent document.
 *
 * Spring sets its CSS custom properties on the main document's body via
 * `SpringProvider` with `setRoot`. Because the site iframe is a separate
 * document, those variables are not directly available inside it.
 * This helper bridges the gap by reading the computed value from the parent.
 */
const getCanvasBackgroundColor = (): string => {
  return getComputedStyle(document.body)
    .getPropertyValue(UI_CANVAS_BACKGROUND_CSS_VAR)
    .trim();
};

/**
 * Custom hook that returns the current `uiCanvasBackground` color value,
 * re-reading it whenever the Spring theme changes.
 */
const useCanvasBackgroundColor = (): string => {
  const {themeClassName} = useThemeContext();
  const [backgroundColor, setBackgroundColor] = React.useState(
    getCanvasBackgroundColor
  );

  // Use changes to `themeClassName` as a signal that the theme has been changed,
  // which means the resolved canvas background color needs to be re-computed.
  React.useEffect(() => {
    setBackgroundColor(getCanvasBackgroundColor());
  }, [themeClassName]);

  return backgroundColor;
};

/**
 * Injects a `<style>` tag into the site iframe that keeps the Open Canvas
 * body background in sync with the designer's `uiCanvasBackground` token.
 *
 * This component is rendered inside the iframe portal (alongside `ViewportStyles`),
 * so the `<style>` tag lands in the iframe's DOM, while `document.body` still refers
 * to the main designer document where Spring CSS variables are available.
 */
export const OpenCanvasBackgroundStyles = () => {
  const backgroundColor =
    useCanvasBackgroundColor() || CANVAS_BACKGROUND_FALLBACK;

  return (
    <style>{`.wf-open-canvas [data-wf-open-canvas-body] { background: ${backgroundColor}; }`}</style>
  );
};
